import { useState } from 'react';
import {
  Card, CardContent, Table, TableHead, TableRow, TableCell, TableBody, Chip, Typography, Autocomplete, TextField, Snackbar, Alert, Box, Stack, CircularProgress,
} from '@mui/material';
import { CheckCircle } from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { dealerInsightsApi, type InsightTeamMember } from '../../api/dealerInsights.api';
import { extractApiError } from '../../api/error';
import { useAuth } from '../../hooks/useAuth';

/**
 * INSIGHTS → Dealer Mapping. Business Heads map dealers to team members for
 * business ownership and performance tracking.
 */
export function DealerMappingPage() {
  const qc = useQueryClient();
  const { hasPermission } = useAuth();
  const [toast, setToast] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const canManage = hasPermission('dealer_insight.manage');

  const { data } = useQuery({ queryKey: ['dealer-ownership'], queryFn: () => dealerInsightsApi.ownership() });
  const members = data?.teamMembers ?? [];

  const setOwner = useMutation({
    mutationFn: ({ dealerId, ownerUserId }: { dealerId: number; ownerUserId: number | null }) => dealerInsightsApi.setOwner(dealerId, ownerUserId),
    onSuccess: (_data, variables) => { setError(null); setToast(variables.ownerUserId ? 'Dealer mapped to team member ✓' : 'Owner unassigned'); qc.invalidateQueries({ queryKey: ['dealer-ownership'] }); },
    onError: (e) => setError(extractApiError(e)),
  });

  return (
    <>
      <PageHeader title="Dealer Mapping" subtitle="Map dealers to team members for ownership & performance tracking." />
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Card variant="outlined"><CardContent>
        <Table size="small">
          <TableHead><TableRow><TableCell>Dealer</TableCell><TableCell>Status</TableCell><TableCell sx={{ width: 460 }}>Owner (team member)</TableCell></TableRow></TableHead>
          <TableBody>
            {(data?.dealers ?? []).map((d) => (
              <TableRow key={d.id} hover>
                <TableCell><Typography fontWeight={600}>{d.dealerName}</Typography><Typography variant="caption" color="text.secondary">{d.dealerCode}</Typography></TableCell>
                <TableCell><Chip size="small" label={d.status} /></TableCell>
                <TableCell>
                  {canManage ? (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Autocomplete
                        size="small"
                        sx={{ flex: 1 }}
                        options={members}
                        getOptionLabel={(m: InsightTeamMember) => m.fullName}
                        isOptionEqualToValue={(a, b) => a.userId === b.userId}
                        value={members.find((m) => m.userId === d.ownerUserId) ?? null}
                        onChange={(_e, v) => setOwner.mutate({ dealerId: d.id, ownerUserId: v?.userId ?? null })}
                        renderInput={(p) => <TextField {...p} placeholder="Unassigned" />}
                      />
                      {setOwner.isPending && setOwner.variables?.dealerId === d.id ? (
                        <Chip size="small" icon={<CircularProgress size={12} />} label="Saving…" />
                      ) : d.ownerUserId ? (
                        <Chip size="small" color="success" icon={<CheckCircle />} label="Mapped" />
                      ) : (
                        <Chip size="small" variant="outlined" label="Unassigned" />
                      )}
                    </Stack>
                  ) : (
                    <Typography variant="body2">{d.ownerName ?? '—'}</Typography>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {(data?.dealers.length ?? 0) === 0 && <TableRow><TableCell colSpan={3}><Box py={2}><Typography variant="body2" color="text.secondary" textAlign="center">No dealers.</Typography></Box></TableCell></TableRow>}
          </TableBody>
        </Table>
      </CardContent></Card>

      <Snackbar open={Boolean(toast)} autoHideDuration={3000} onClose={() => setToast(null)}><Alert severity="success" onClose={() => setToast(null)}>{toast}</Alert></Snackbar>
    </>
  );
}
